import { createTheme, defaultVariantColorsResolver, MantineColorsTuple, parseThemeColor, VariantColorsResolver } from '@mantine/core'

// BDC-AI Brand Colors with Material 3 Tonal Palettes
// Primary - Deep Blue #1A2B6D (BDC-AI brand color)
const blue: MantineColorsTuple = [
  '#E8EBF5',  // 95
  '#D1D7EB',  // 90
  '#A3AFD7',  // 80
  '#7587C3',  // 70
  '#475FAF',  // 60
  '#1A2B6D',  // 50 - Primary (BDC-AI)
  '#162360',  // 40
  '#121B53',  // 30
  '#0E1346',  // 20
  '#0A0B39',  // 10
]

// Accent - Vibrant Orange #FF6F00 (BDC-AI accent)
const orange: MantineColorsTuple = [
  '#FFF3E0',  // 95
  '#FFE0B2',  // 90
  '#FFCC80',  // 80
  '#FFB74D',  // 70
  '#FFA726',  // 60
  '#FF6F00',  // 50 - Accent (BDC-AI)
  '#F57C00',  // 40
  '#EF6C00',  // 30
  '#E65100',  // 20
  '#BF360C',  // 10
]

// Secondary - Cyan/Blue (for pending, info states)
const cyan: MantineColorsTuple = [
  '#E0F7FA',  // 95
  '#B2EBF2',  // 90
  '#80DEEA',  // 80
  '#4DD0E1',  // 70
  '#26C6DA',  // 60
  '#00BCD4',  // 50 - Secondary
  '#00ACC1',  // 40
  '#0097A7',  // 30
  '#00838F',  // 20
  '#006064',  // 10
]

// Success - Green (for complete, success states)
const green: MantineColorsTuple = [
  '#E8F5E9',  // 95
  '#C8E6C9',  // 90
  '#A5D6A7',  // 80
  '#81C784',  // 70
  '#66BB6A',  // 60
  '#4CAF50',  // 50 - Success
  '#43A047',  // 40
  '#388E3C',  // 30
  '#2E7D32',  // 20
  '#1B5E20',  // 10
]

// Error - Red (for reportable, error states)
const red: MantineColorsTuple = [
  '#FFEBEE',  // 95
  '#FFCDD2',  // 90
  '#EF9A9A',  // 80
  '#E57373',  // 70
  '#EF5350',  // 60
  '#F44336',  // 50 - Error
  '#E53935',  // 40
  '#D32F2F',  // 30
  '#C62828',  // 20
  '#B71C1C',  // 10
]

// Neutral - Gray (for text, borders, backgrounds)
const gray: MantineColorsTuple = [
  '#FAFAFA',  // 99
  '#F5F5F5',  // 95
  '#EEEEEE',  // 90
  '#E0E0E0',  // 80
  '#BDBDBD',  // 70
  '#9E9E9E',  // 60
  '#757575',  // 50
  '#616161',  // 40
  '#424242',  // 30
  '#212121',  // 10
]

// Custom variant resolver for Material 3 style
const variantColorResolver: VariantColorsResolver = (input) => {
  const defaultResolvedColors = defaultVariantColorsResolver(input)
  const parsedColor = parseThemeColor({
    color: input.color || input.theme.primaryColor,
    theme: input.theme,
  })

  // BDC-AI brand styling - Deep Blue primary
  if (parsedColor.isThemeColor && parsedColor.color === 'blue' && input.variant === 'filled') {
    return {
      background: input.theme.colors.blue[5],
      hover: input.theme.colors.blue[6],
      color: '#fff',
      border: 'none',
    }
  }

  // Orange accent styling
  if (parsedColor.isThemeColor && parsedColor.color === 'orange' && input.variant === 'filled') {
    return {
      background: input.theme.colors.orange[5],
      hover: input.theme.colors.orange[6],
      color: '#fff',
      border: 'none',
    }
  }

  return defaultResolvedColors
}

export const theme = createTheme({
  /** Default corner radius for components that don’t set `radius` */
  defaultRadius: 'sm',

  /** Color scheme */
  primaryColor: 'blue',  // BDC-AI Deep Blue
  
  /** Colors */
  colors: {
    blue,
    orange,
    cyan,
    green,
    red,
    gray,
  },

  /** Other settings */
  other: {
    // Content area always light (matches global.css AppShell main)
    contentBackground: '#f4f5f7',
    contentTextColor: '#212121',
    
    // Material 3 Dark Theme Colors (guaranteed contrast)
    darkSurface: '#1C1B1F',           // Main dark background
    darkSurfaceContainer: '#211F26',   // Elevated surface
    darkSurfaceContainerHigh: '#2B2930', // Higher elevation
    darkSurfaceContainerHighest: '#36343B', // Highest elevation
    darkOnSurface: '#E6E1E5',          // Primary text on dark
    darkOnSurfaceVariant: '#CAC4D0',   // Secondary text on dark
    darkOutline: '#938F99',            // Borders on dark
    darkOutlineVariant: '#49454F',     // Subtle borders on dark
  },

  /** Typography — Inter UI + monospace for technical strings */
  fontFamily:
    'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif',
  fontFamilyMonospace:
    '"JetBrains Mono", ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
  headings: {
    fontFamily:
      'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif',
    fontWeight: '500',
    sizes: {
      /* Compact scale — less vertical space while staying legible */
      h1: { fontSize: '1.75rem', lineHeight: '1.2', fontWeight: '600' },
      h2: { fontSize: '1.25rem', lineHeight: '1.25', fontWeight: '600' },
      h3: { fontSize: '1.125rem', lineHeight: '1.3', fontWeight: '600' },
      h4: { fontSize: '1rem', lineHeight: '1.35', fontWeight: '600' },
      h5: { fontSize: '0.9375rem', lineHeight: '1.4', fontWeight: '600' },
      h6: { fontSize: '0.875rem', lineHeight: '1.4', fontWeight: '600' },
    },
  },

  /** Spacing */
  spacing: {
    xs: '0.5rem',   // 8px
    sm: '0.75rem',  // 12px
    md: '1rem',     // 16px
    lg: '1.5rem',   // 24px
    xl: '2rem',     // 32px
  },

  /** Border radius — tighter, industrial (borders over pills) */
  radius: {
    xs: '0.125rem', // 2px
    sm: '0.25rem', // 4px
    md: '0.375rem', // 6px
    lg: '0.5rem', // 8px
    xl: '0.75rem', // 12px
  },

  /** Shadows — lighter; structure from borders where possible */
  shadows: {
    xs: '0 1px 2px rgba(0, 0, 0, 0.04)',
    sm: '0 1px 3px rgba(0, 0, 0, 0.06)',
    md: '0 2px 8px rgba(0, 0, 0, 0.08)',
    lg: '0 4px 12px rgba(0, 0, 0, 0.1)',
    xl: '0 8px 24px rgba(0, 0, 0, 0.12)',
  },

  /** Component defaults */
  cursorType: 'pointer',
  
  /** Variant resolver */
  variantColorResolver,

  /** White and Black */
  white: '#FFFFFF',
  black: '#000000',

  /** Component-specific styling */
  components: {
    Button: {
      defaultProps: {
        radius: 'sm',
      },
      styles: {
        root: {
          fontWeight: 600,
          letterSpacing: '0.01em',
        },
      },
    },
    Card: {
      defaultProps: {
        radius: 'md',
        withBorder: true,
        shadow: 'xs',
      },
    },
    Select: {
      defaultProps: {
        radius: 'sm',
      },
    },
    TextInput: {
      defaultProps: {
        radius: 'sm',
      },
    },
    Badge: {
      defaultProps: {
        radius: 'sm',
        size: 'sm',
      },
      styles: {
        root: {
          textTransform: 'uppercase',
          fontWeight: 600,
          fontSize: '0.6875rem',
          letterSpacing: '0.05em',
          paddingLeft: '0.5rem',
          paddingRight: '0.5rem',
        },
      },
    },
    Paper: {
      defaultProps: {
        radius: 'sm',
        shadow: 'xs',
      },
    },
    Title: {
      styles: {
        root: {
          marginTop: 0,
          marginBottom: 0,
        },
      },
    },
  },
})

